import styled from 'styled-components';
import SearchFilter from './filter/SearchFilter';
import { FilterType, Region } from './FilterData';
import { useEffect, useRef, useState } from 'react';
import SelectedOption from './filter/SelectedOption';
import { isLogin } from '../../utils/auth';
import { useModal } from '../../context/ModalContext';
import SaveFilter from './saveFilter/SaveFilter';
import { useNavigate } from 'react-router-dom';

interface Props {
  filters: FilterType;
  sort: {
    value: string;
    label: string;
  };
  setFilters: (filters: FilterType) => void;
  getList: (
    offset: number,
    sort: {
      value: string;
      label: string;
    },
    filters?: any,
  ) => void;
  setOffset: (offset: number) => void;
}

const minmaxObjectFields = [
  'appraisalPrice',
  'minPrice',
  'buildingArea',
  'landArea',
] as const;

const startendObjectFields = ['saleDate', 'approvalDate'] as const;

function SearchTop({ filters, setFilters, getList, sort, setOffset }: Props) {
  const { modalDispatch } = useModal();
  const navigate = useNavigate();
  const [moreOptionCount, setMoreOptionCount] = useState(0);
  const [openSavefilter, setOpenSavefilter] = useState(false);

  useEffect(() => {
    let c = 0;

    minmaxObjectFields.forEach((key) => {
      const value = filters[key];
      if (value.min !== '' || value.max !== '') {
        c += 1;
      }
    });

    startendObjectFields.forEach((key) => {
      const value = filters[key];
      if (value.start !== '' || value.end !== '') {
        c += 1;
      }
    });

    if (filters.bidno !== '') {
      c += 1;
    }

    c += filters.useArea.length;
    c += filters.specialItemCondition.length;

    setMoreOptionCount(c);
  }, [filters]);

  const prevFiltersRef = useRef(filters);

  const isOnlyProgressStatusChangedToAll = () => {
    const prev = prevFiltersRef.current;

    if (filters.progressStatus !== '전체') return false;
    const { progressStatus: _, region, ...rest } = filters;
    const { progressStatus: __, region: prevRegion, ...prevRest } = prev;

    const same = JSON.stringify(rest) === JSON.stringify(prevRest);
    const isRegionChange =
      region.length === 0 ||
      (region.length === 1 &&
        region[0].sido === '' &&
        region[0].sigungu === '' &&
        region[0].emd === '');

    return same && isRegionChange;
  };

  const handleReset = () => {
    setFilters({
      type: 'a',
      year: '',
      caseNumber: '',
      regionOrCourt: 'r',
      region: [] as Region[],
      address: '',
      court: '',
      itemStatus: [],
      multiItemStatus: [],
      appraisalPrice: { min: '', max: '' },
      minPrice: { min: '', max: '' },
      saleDate: { start: '', end: '' },
      approvalDate: { start: '', end: '' },
      buildingArea: { min: '', max: '' },
      landArea: { min: '', max: '' },
      bidno: '',
      progressStatus: '진행',
      useArea: [],
      multiUseArea: [],
      specialConditionViewOption: 'o',
      specialItemCondition: [],
      multiSpecialItemCondition: [],
      holdingCompany: [],
      multiHoldingCompany: [],
    });
  };

  return (
    <Container>
      <MySearchListButton
        onClick={() => {
          if (!isLogin()) {
            modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
            return;
          }
          navigate(`/search/mySearchOption?type=${filters.type}`);
        }}
      >
        내 검색조건 불러오기
      </MySearchListButton>

      <SearchFilter
        filters={filters}
        setFilters={setFilters}
        moreOptionCount={moreOptionCount}
      />
      {moreOptionCount > 0 && (
        <SelectedOption filters={filters} setFilters={setFilters} />
      )}
      <ButtonBox>
        <SaveButton
          onClick={() => {
            if (!isLogin()) {
              modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
              return;
            }
            setOpenSavefilter(true);
          }}
        >
          저장
        </SaveButton>
        <ResetButton onClick={handleReset}>
          <img src="/assets/images/icon/reset.svg" alt="" />
          <span>초기화</span>
        </ResetButton>
        <ApplyButton
          onClick={() => {
            if (!isLogin()) {
              modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
              return;
            }
            if (isOnlyProgressStatusChangedToAll()) {
              window.alert(
                `진행단계 '전체'를 선택할 경우, 추가 조건 입력이 필요합니다.\n검색 조건을 추가해주세요.`,
              );
              return;
            }

            getList(0, sort, filters);
            setOffset(0);
          }}
        >
          검색
        </ApplyButton>
      </ButtonBox>
      {openSavefilter && (
        <SaveFilter
          filters={filters}
          setFilters={setFilters}
          onClose={() => setOpenSavefilter(false)}
        />
      )}
    </Container>
  );
}

export default SearchTop;

const Container = styled.div`
  width: 100%;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: #fff;
`;

const MySearchListButton = styled.button`
  border-radius: 8px;
  padding: 12px;
  background-color: #70737c14;
  border: none;
  cursor: pointer;

  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222222;
`;

const ButtonBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const SaveButton = styled.button`
  border-radius: 8px;
  border: 1px solid #008aff;
  padding: 12px 16px;

  font-weight: 500;
  font-size: 16px;
  text-align: center;
  color: #008aff;
  background-color: #ffffff;
`;

const ResetButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #e1e2e4;
  background-color: #fff;
  font-size: 12px;
  font-weight: 400;
  color: #616161;
  cursor: pointer;
  padding: 7px 14px;
`;
const ApplyButton = styled.button`
  flex: 1;
  border-radius: 8px;
  border: none;
  padding: 12px 28px;
  background-color: #008aff;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
`;
